import { useState } from "react";
import { api } from "@/lib/api";
import { Upload, Loader2, Check, X, Scan, Pencil } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["body_fat_pct", "Body fat", "%"],
  ["skeletal_muscle_kg", "Skeletal muscle", "kg"],
  ["lean_mass_kg", "Lean mass", "kg"],
  ["body_weight_kg", "Body weight", "kg"],
  ["bone_density", "Bone density", "g/cm²"],
  ["visceral_fat", "Visceral fat", "lvl"],
];

// Upload a DEXA scan (PDF/image/CSV/Excel) -> AI extracts fields -> review & edit -> save.
// Coaches can also open the form empty and enter/edit every stat by hand.
export default function DexaImport({ athleteId, current, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null); // {field: value}
  const [meta, setMeta] = useState(null); // {confidence, summary, filename, scan_date, disclaimer, cost}

  const openManual = () => {
    const d = {};
    FIELDS.forEach(([k]) => (d[k] = current?.[k] ?? ""));
    setDraft(d);
    setMeta({ manual: true });
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setDraft(null); setMeta(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/athletes/${athleteId}/media/dexa`, fd, {
        timeout: 180000, headers: { "Content-Type": "multipart/form-data" },
      });
      const d = data.draft || {};
      const dd = {};
      FIELDS.forEach(([k]) => (dd[k] = d[k] ?? ""));
      setDraft(dd);
      setMeta({ confidence: d.confidence, summary: d.summary, filename: data.filename,
        scan_date: d.scan_date, disclaimer: data.disclaimer, cost: data.cost });
      toast.success(`Scan read${data.cost ? ` · ${data.cost} credits used` : ""} · review every field`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the scan");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { source: "DEXA" };
      FIELDS.forEach(([k]) => { if (draft[k] !== "" && draft[k] != null) payload[k] = Number(draft[k]); });
      if (meta?.scan_date) payload.date = meta.scan_date;
      await api.post(`/athletes/${athleteId}/body-composition`, payload);
      toast.success("Body composition saved");
      setDraft(null); setMeta(null); onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="dexa-import">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs bg-foreground text-background px-3 py-1.5 cursor-pointer hover:bg-neutral-800 transition-colors flex items-center gap-1.5" data-testid="dexa-upload-label">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scan className="w-3.5 h-3.5" />}
          {busy ? "Reading scan…" : "Upload DEXA scan"}
          <input type="file" className="hidden" onChange={upload} disabled={busy}
            data-testid="dexa-input" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.csv,.txt,.xlsx,.xls" />
        </label>
        <button type="button" data-testid="dexa-manual-btn" onClick={openManual}
          className="text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5">
          <Pencil className="w-3.5 h-3.5" /> Enter / edit manually
        </button>
        <span className="text-[11px] text-muted-foreground">PDF · image · CSV · best-effort AI, review before saving</span>
      </div>

      {draft && (
        <div className="mt-4 border border-border p-4" data-testid="dexa-review">
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">
              {meta?.manual ? "Manual entry" : `Extracted${meta?.filename ? ` · ${meta.filename}` : ""}`}
            </span>
            {meta?.confidence != null && (
              <span className={`text-[10px] tracking-[0.15em] uppercase font-bold ${meta.confidence >= 70 ? "text-traffic-green" : meta.confidence >= 40 ? "text-amber-500" : "text-traffic-red"}`} data-testid="dexa-confidence">
                Confidence {meta.confidence}%
              </span>
            )}
            {meta?.scan_date && <span className="text-[11px] text-muted-foreground">Scan date {meta.scan_date}</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {FIELDS.map(([k, label, unit]) => (
              <label key={k} className="block">
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">{label} <span className="normal-case tracking-normal">({unit})</span></span>
                <input type="number" step="0.1" value={draft[k] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                  data-testid={`dexa-field-${k}`}
                  className="mt-1 w-full bg-background border border-border px-2 py-1.5 text-sm tabular-nums" />
              </label>
            ))}
          </div>
          {meta?.summary && <p className="text-xs text-muted-foreground mt-3">{meta.summary}</p>}
          <p className="text-[11px] text-muted-foreground mt-2">{meta?.disclaimer || "Review and correct every field before saving. Not medical advice."}</p>
          <div className="flex gap-3 mt-4">
            <button data-testid="dexa-save" onClick={save} disabled={saving}
              className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors flex items-center gap-1.5 disabled:opacity-40">
              <Check className="w-4 h-4" /> {saving ? "Saving…" : "Save body composition"}
            </button>
            <button data-testid="dexa-discard" onClick={() => { setDraft(null); setMeta(null); }}
              className="text-sm px-4 py-2 border border-border hover:bg-surface transition-colors flex items-center gap-1.5">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
